import { JSX, Match, Show, Switch, createEffect, createMemo, createSignal, onMount } from 'solid-js'
import { toast } from 'common'
import { visitorCheckin as api } from 'st-app/api'
import { requireVisitorCheckinContext } from '../VisitorCheckinContext'
import { requireStepContext } from '../../st-wizard/st-wizard'
import Loader from '../../st-loader/st-loader'
import Button from '../../st-button/st-button'
import { showDialog } from '../../st-modal/st-modal'
import { formatAddress, formatDob, formatGender as formatSex, formatHeight, formatName } from './util'
import styles from './verification-step.module.css'

export const VerificationStep = () => {
    const checkin = requireVisitorCheckinContext();
    const step = requireStepContext();

    const [isProcessing, setIsProcessing] = createSignal(false);

    onMount(() => {
        if (checkin.verificationStatus() === 'pending' && !checkin.isVerifying()) {
            checkin.verify();
        }
    })

    createEffect(() => {
        if (checkin.verificationStatus() === 'verified' && !step.completed() && !offender()) {
            setTimeout(() => {
                step.setCompleted(true);
                step.next();
            }, 1000)
        }
    })

    const offender = createMemo(() => checkin.offenders()?.at(0))


    const confirmMatch = async () => {
        if (isProcessing()) return;

        setIsProcessing(true);
        try {
            const { message } = await api.DenyVisitor(checkin.vmGU()!);
            toast.notify('success', 'The visitor has been denied entry and the incident has been logged. Notifications have been sent to the configured contacts.');
            showDialog(() => (
                <div>
                    <p innerHTML={message}></p>
                </div>
            ), {
                title: 'Visitor Denied',
                draggable: false,
            })
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error occurred while denying the visitor.');
        }

        if (import.meta.env.DEV) {
            toast.notify('error', '(DEV mode, modal not closed)');
        } else {
            if (window.ST) {
                window.ST.CloseModal();
                window.ST.RefreshView();
            }
        }
        setIsProcessing(false);
    }

    const rejectMatch = async () => {
        if (isProcessing()) return;

        setIsProcessing(true);
        try {
            await api.ApproveVisitor(checkin.vmGU()!);
            checkin.setVerificationStatus('verified');
            step.setCompleted(true);
            step.next();
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error occurred while clearing the visitor.');
        }
        setIsProcessing(false);
    }

    return (
        <div class={styles['verification-step']}>
            <Switch>
                <Match when={checkin.verificationStatus() === 'verified' && !offender()}>
                    <span role="presentation" class={styles['verified']}>
                        <svg width="128" height="128" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle
                                class={`${styles['draw-path']} ${styles['circle']}`}
                                pathLength={1}
                                cx="24"
                                cy="24"
                                r="20"
                                fill="transparent"
                                stroke-width="4"
                                stroke="currentColor"
                            />
                            <path
                                class={`${styles['draw-path']} ${styles['check']}`}
                                pathLength={1}
                                d="M16 24 L22 30 L32 20"
                                stroke="currentColor"
                                stroke-width="4"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </span>
                    {checkin.verificationStatusMessage()}
                </Match>
                <Match when={checkin.verificationStatus() === 'dangerous'}>
                    <div class={styles['danger-banner']}>
                            <i class={`fas fa-siren-on`}/>
                            <div style='display: flex; flex-direction: column; align-items: center; font-size: 0.7em;'>
                                <span style='font-size: 1em; font-weight: bold;'>The scanned ID was flagged as a potential match with a known offender.</span>
                                <div style='font-size: 0.8em; font-style: italic; opacity: 0.9;'>
                                    Possible match determined based on First Name, Last Name, and Date of Birth.
                                </div>
                            </div>
                            <i class={`fas fa-siren-on `}/>
                    </div>
                </Match>
                <Match when={checkin.verificationStatus() === 'pending' || checkin.isVerifying()}>
                    <Loader size='large'/>
                    {checkin.verificationStatusMessage()}
                </Match>
            </Switch>

            <Show when={checkin.offenders().length > 0}>
                <div class={styles['offender-comparison']}>
                    <div class={styles['comparison-panel']}>
                        <span class={styles['comparison-panel-title']}>Scanned Info</span>

                        <div class={styles['comparison-top-panel']}>
                            <div class={styles['comparison-image']}>
                                <img src={`data:image/bmp;base64,${checkin.scan()?.photoBase64}`}></img>
                            </div>
                            <div class={styles['comparison-info']}>
                                <span class={styles['comparison-name']}>{formatName(checkin.scan()?.AAMVA)}</span>
                                <Field label="Date of Birth" value={formatDob(checkin.scan()?.AAMVA?.dateOfBirth)} />
                                <Field label="Sex" value={formatSex(checkin.scan()?.AAMVA?.sex)} />
                                <Field label="Hair Color" value={offender()?.hairColor} />
                                <Field label="Eye Color" value={offender()?.eyeColor} />
                                <Field label="Height" value={formatHeight(offender()?.height)} />
                                <Field label="Address" value={formatAddress(offender())} />
                            </div>
                        </div>

                        <div class={styles['drivers-license']}>
                            <img style={{
                                'aspect-ratio': '3.375/2.125',
                                'border-radius': '8px',
                                height: '200px',
                            }} src={`data:image/bmp;base64,${checkin.scan()?.idBase64}`}></img>
                        </div>
                    </div>

                    <div class={styles['comparison-panel']}>
                        <span class={styles['comparison-panel-title']}>Potential Match</span>
                        <div class={styles['comparison-top-panel']}>
                            <div class={styles['comparison-image']}>
                                <img src={offender()?.offenderImageUrl} />
                            </div>

                            <div class={styles['comparison-info']}>
                                <span class={styles['comparison-name']}>{offender()?.name}</span>

                                <Field label="Date of Birth" value={formatDob(offender()?.dob)} />
                                <Field label="Sex" value={formatSex(offender()?.sex)} />
                                <Field label="Hair Color" value={offender()?.hairColor} />
                                <Field label="Eye Color" value={offender()?.eyeColor} />
                                <Field label="Height" value={formatHeight(offender()?.height)} />
                                <Field label="Address" value={formatAddress(offender())} />
                            </div>

                        </div>

                        <div class={styles['offender-actions']}>
                            <Button icon='fas fa-bell-slash' type='success' onClick={rejectMatch}>False Alarm</Button>
                            <Button icon='fas fa-siren-on' type='danger' onClick={confirmMatch}>Confirm Match</Button>
                        </div>
                    </div>
                </div>
            </Show>
        </div>
    )
}

const Field = (props: { label: string, value: JSX.Element | string | undefined }) => {
    return (
        <div class={styles['field']}>
            <span class={styles['label']}>{props.label}:</span>
            <span class={styles['value']}>{props.value ?? 'N/A'}</span>
        </div>
    )
}

